import classNames from "classnames";
import type { Location } from "history";
import { useMemo } from "react";
import { NavLink } from "react-router-dom";
import type { Agent } from "../../common/types/Agent";
import type { MemberGroupKey, TasksGroupKey } from "../../common/types/FilterGroups";
import UnreadMessagesIndicator from "../Common/UnreadMessagesIndicator";
import "./MenuLinks.css";
import { useGet } from "@albertine/api-client";
import { useClaims } from "../../utils/claims.util";
import routes from "../../utils/routes";
import Label from "../Common/Label";

type MenuItem = {
    link:
        | "/agents/"
        | "/members/"
        | "/messages/all"
        | "/catalog"
        | "/themes"
        | `/tasks/${TasksGroupKey}?${string}`
        | `/requests/all?${string}`
        | `/requests/my-requests?${string}`
        | `/requests/closed?${string}`
        | `/members/${MemberGroupKey}`
        | { pathname: string; state: Location }
        | `/payments?${string}`
        | "/settings/";
    spacer: boolean;
    messageIndicator: boolean;
    title?: string;
    indent?: string;
    count?: string | number;
    overdueCount?: number;
    overdueSoonCount?: number;
};

type Props = {
    currentAgent: Agent;
};

const MenuLinks = function MenuLinks(props: Props) {
    const { currentAgent } = props;
    const { isAdmin } = useClaims();

    const { data: recentConversations } = useGet(
        "recentConversations",
        { query: {} },
        {
            refetchInterval: 5000,
        },
    );
    const { data: dueRequestsCount } = useGet(
        "dueRequestsCount",
        {},
        {
            refetchInterval: 5000,
        },
    );

    let totalUnreadMessages = 0;
    for (const conversation of recentConversations?.items ?? []) {
        totalUnreadMessages += conversation.conversation.unreadMessagesCountByAgent;
    }

    const menuItems: MenuItem[] = useMemo(() => {
        const items: MenuItem[] = [
            {
                link: "/messages/all",
                title: "Messages",
                spacer: false,
                count: totalUnreadMessages > 0 ? totalUnreadMessages : undefined,
                messageIndicator: true,
            },
            {
                link: "/requests/all?statusList=draft&statusList=new&statusList=open&statusList=confirmed",
                title: "Requests",
                spacer: false,
                messageIndicator: false,
            },
            {
                link: `/requests/my-requests?assignedTo=${currentAgent.id}&statusList=draft&statusList=new&statusList=open&statusList=confirmed`,
                indent: "My requests",
                spacer: false,
                messageIndicator: false,
                overdueCount: dueRequestsCount?.overdue ?? 0,
                overdueSoonCount: dueRequestsCount?.dueInTheNextHour ?? 0,
            },
            {
                link: "/requests/closed?statusList=closed",
                indent: "Closed",
                spacer: false,
                count: undefined,
                messageIndicator: false,
            },
            {
                link: "/members/default",
                title: "Members",
                spacer: false,
                messageIndicator: false,
            },
            {
                link: "/members/assigned-to-me",
                indent: "My members",
                spacer: false,
                messageIndicator: false,
            },
            {
                link: "/members/lapsed-and-churned",
                indent: "Lapsed and churned",
                spacer: false,
                messageIndicator: false,
            },
            {
                link: "/members/archived",
                indent: "Archived",
                spacer: false,
                count: undefined,
                messageIndicator: false,
            },
            {
                link: "/tasks/open?mentionStatus[0]=open&sortBy=mentionDueDate&sortOrder=asc",
                title: "Tasks",
                spacer: false,
                count: undefined,
                messageIndicator: false,
            },
            {
                link: "/tasks/resolved?mentionStatus[0]=resolved&sortBy=mentionDueDate&sortOrder=asc",
                indent: "Resolved",
                spacer: false,
                count: undefined,
                messageIndicator: false,
            },
            {
                link: "/catalog",
                title: "Catalog",
                spacer: false,
                count: undefined,
                messageIndicator: false,
            },
            {
                link: "/themes",
                indent: "Content themes",
                spacer: false,
                count: undefined,
                messageIndicator: false,
            },
        ];
        if (isAdmin) {
            items.push({
                link: "/payments?sortBy=createdAt&sortOrder=desc",
                title: "Payments",
                spacer: false,
                count: undefined,
                messageIndicator: false,
            });
        }
        items.push({
            link: "/agents/",
            title: "Agents",
            spacer: false,
            count: undefined,
            messageIndicator: false,
        });
        if (isAdmin) {
            items.push({
                link: "/settings/",
                title: "Settings",
                spacer: false,
                count: undefined,
                messageIndicator: false,
            });
        }
        return items;
    }, [totalUnreadMessages, dueRequestsCount, currentAgent, isAdmin]);

    return (
        <div className="tab main-menu">
            {menuItems.map((item, index) => (
                <div
                    key={index}
                    className={classNames(
                        "main-menu__action",
                        { "main-menu__title": item.title },
                        { "main-menu__indent": item.indent },
                        { "main-menu__spacer": item.spacer },
                    )}
                >
                    {/* @ts-ignore we are using outdated libraries that use ReactNode instead of JSX.Element. */}
                    <NavLink
                        to={item.link}
                        activeClassName="active"
                        isActive={(_, location) =>
                            item.link.toString().includes(location.pathname)
                        }
                    >
                        {item.title && (
                            <Label.Medium.SemiBold>{item.title}</Label.Medium.SemiBold>
                        )}
                        {item.indent && (
                            <Label.Small.N50>{item.indent}</Label.Small.N50>
                        )}
                        <span className="main-menu__indicators">
                            {item.overdueCount ? (
                                <UnreadMessagesIndicator
                                    count={item.overdueCount}
                                    type="overdue"
                                />
                            ) : item.overdueSoonCount ? (
                                <UnreadMessagesIndicator
                                    count={item.overdueSoonCount}
                                    type="overdue-soon"
                                />
                            ) : null}
                            {item.count && (
                                <UnreadMessagesIndicator
                                    count={item.count || 0}
                                    type={item.messageIndicator ? "unread" : "info"}
                                />
                            )}
                        </span>
                    </NavLink>
                </div>
            ))}
        </div>
    );
};

export default MenuLinks;
